/* eslint-disable react/prop-types, react/jsx-props-no-spreading */
import React from 'react';
import { Row } from 'antd';
import castArray from 'lodash/castArray';
import memoize from 'lodash/memoize';

import { Typography, Sortable, Button } from '@atoms';
import { useSet } from '@hooks';

import s from './styles.module.less';

const SortableList = ({
  name,
  label,
  extra,
  subtitle,
  titleLevel = 3,
  withPlaceholder,
  children,
  withCount,
  value = [],
  onChange,
  useEditable = useSet,
}) => {
  const droppableId = React.useMemo(() => {
    return `droppable-${castArray(name).join('.')}-${Math.random()}`;
  }, [name]);

  const [, editable] = useEditable();

  const handleToggleEdit = React.useMemo(
    () =>
      memoize((val) => () => {
        editable.toggle(val);
      }),
    [editable],
  );

  const handleOnSubmit = React.useMemo(
    () =>
      memoize((val) => (newval) => {
        const idx = value.indexOf(val);
        // eslint-disable-next-line no-unused-expressions
        onChange?.([...value.slice(0, idx), newval, ...value.slice(idx + 1)]);
        editable.delete(val);
      }),
    [value, onChange, editable],
  );

  const handleOnDelete = React.useMemo(
    () =>
      memoize((val) => () => {
        const idx = value.indexOf(val);
        // eslint-disable-next-line no-unused-expressions
        onChange?.([...value.slice(0, idx), ...value.slice(idx + 1)]);
        editable.delete(val);
      }),
    [value, onChange, editable],
  );

  const handleOnMove = React.useCallback(
    ({ source, destination }) => {
      const initialItems = [...value];
      const movedItem = initialItems.splice(source.index, 1);
      // eslint-disable-next-line no-unused-expressions
      onChange?.([...initialItems.slice(0, destination.index), ...movedItem, ...initialItems.slice(destination.index)]);
    },
    [value, onChange],
  );

  const renderButtons = React.useMemo(
    () =>
      memoize((val) => ({ onSubmit, onCancel, onDelete }) => (
        <div className={s.buttons}>
          <Button type="customBlue" style={{ minWidth: 200 }} onClick={onSubmit}>
            SAVE CHANGES
          </Button>
          <Button key="back" onClick={onCancel || handleToggleEdit(val)}>
            CANCEL
          </Button>
          <Button key="delete" onClick={onDelete || handleOnDelete(val)} className="delete-button">
            DELETE
          </Button>
        </div>
      )),
    [handleOnDelete, handleToggleEdit],
  );

  return (
    <>
      <Row align="middle" justify="space-between">
        <Typography.Title level={titleLevel} note={withCount && (value?.length || 0)}>
          {label}
        </Typography.Title>
        {extra}
      </Row>

      {subtitle && (
        <div style={{ marginBottom: 30 }}>
          <Typography.Text level={3}>{subtitle}</Typography.Text>
        </div>
      )}

      <Sortable.List onDragEnd={handleOnMove} droppableId={droppableId} placeholder={withPlaceholder && !value.length}>
        {value.map((val, index) => {
          const key = `${val?.id}-${index}`;
          const isEdit = editable.has(val);
          const content = children(val, {
            key,
            edit: handleToggleEdit(val),
            remove: handleOnDelete(val),
            buttons: isEdit ? renderButtons(val) : undefined,
            save: isEdit ? handleOnSubmit(val) : undefined,
            isEdit,
          });

          return isEdit ? (
            <div key={key} className={s.editPanel}>
              {content}
            </div>
          ) : (
            <Sortable.ItemWithDelete key={key} draggableId={key} index={index} onDelete={handleOnDelete(val)}>
              {content}
            </Sortable.ItemWithDelete>
          );
        })}
      </Sortable.List>
    </>
  );
};

export default SortableList;
